import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import india from "../data/india.json";

export const STATES = india.states.map((s) => s.state);

const input = "mt-1.5 w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F] focus:shadow-[0_0_0_3px_rgba(126,30,63,0.1)] transition-[border-color,box-shadow] placeholder:text-[#857078]/50";
const lbl = "text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]";

export const EMPTY_ADDRESS = { name: "", phone: "", house: "", street: "", landmark: "", state: "", district: "", city: "", pincode: "", is_default: false };

export function validateAddress(a) {
  if (a.name.trim().length < 2) return "Please enter the full name";
  if (!/^\d{10}$/.test(a.phone)) return "Please enter a valid 10-digit mobile number";
  if (!a.house.trim()) return "Please enter house / flat / building";
  if (!a.street.trim()) return "Please enter street / area";
  if (!a.state) return "Please select a state";
  if (!a.district) return "Please select a district";
  if (!a.city.trim()) return "Please enter city / area";
  if (!/^\d{6}$/.test(a.pincode)) return "Please enter a valid 6-digit PIN code";
  return null;
}

export default function AddressForm({ value, onChange, testid = "address-form" }) {
  const [stateQuery, setStateQuery] = useState("");
  const a = value;
  const set = (k, v) => onChange({ ...a, [k]: v });

  const districts = useMemo(() => {
    const st = india.states.find((s) => s.state === a.state);
    return st ? st.districts : [];
  }, [a.state]);

  const stateOptions = useMemo(() => {
    const q = stateQuery.trim().toLowerCase();
    return q ? STATES.filter((s) => s.toLowerCase().includes(q)) : STATES;
  }, [stateQuery]);

  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className={lbl}>Full Name *</span>
          <input className={input} value={a.name} onChange={(e) => set("name", e.target.value)} data-testid="addr-name" /></label>
        <label className="block"><span className={lbl}>Mobile Number *</span>
          <div className="flex items-center gap-0 mt-1.5">
            <span className="bg-[#FBF7F1] border border-r-0 border-[#E5E7EB] rounded-l-xl px-3 py-3 text-sm text-[#857078]">+91</span>
            <input className={`${input} !mt-0 rounded-l-none`} inputMode="numeric" maxLength={10} value={a.phone}
              onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" data-testid="addr-phone" /></div>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className={lbl}>House / Flat / Building *</span>
          <input className={input} value={a.house} onChange={(e) => set("house", e.target.value)} data-testid="addr-house" /></label>
        <label className="block"><span className={lbl}>Street / Area *</span>
          <input className={input} value={a.street} onChange={(e) => set("street", e.target.value)} data-testid="addr-street" /></label>
      </div>
      <label className="block"><span className={lbl}>Landmark (optional)</span>
        <input className={input} value={a.landmark} onChange={(e) => set("landmark", e.target.value)} data-testid="addr-landmark" /></label>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <span className={lbl}>State *</span>
          <input className={input} placeholder="Type to search — e.g. Del" value={stateQuery}
            onChange={(e) => { setStateQuery(e.target.value); }} data-testid="addr-state-search" />
          <select className={`${input} !mt-2`} value={a.state}
            onChange={(e) => { set("state", e.target.value); set("district", ""); set("city", ""); }} data-testid="addr-state">
            <option value="">Select state</option>
            {stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <label className="block"><span className={lbl}>District *</span>
          <select className={input} value={a.district} disabled={!a.state}
            onChange={(e) => set("district", e.target.value)} data-testid="addr-district">
            <option value="">{a.state ? "Select district" : "Select state first"}</option>
            {districts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className={lbl}>City / Area *</span>
          <input className={input} value={a.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Sector 62, Noida" data-testid="addr-city" /></label>
        <label className="block"><span className={lbl}>PIN Code *</span>
          <input className={input} inputMode="numeric" maxLength={6} value={a.pincode}
            onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit PIN" data-testid="addr-pincode" />
          {/^\d{6}$/.test(a.pincode) && <span className="text-xs text-[#2A9D8F] flex items-center gap-1 mt-1"><Check size={12} /> Valid PIN format</span>}
        </label>
      </div>
      <label className="flex items-center gap-2.5 text-sm text-[#45303A] cursor-pointer">
        <input type="checkbox" className="accent-[#7E1E3F] w-4 h-4" checked={a.is_default} onChange={(e) => set("is_default", e.target.checked)} data-testid="addr-default" />
        Set as my default delivery address
      </label>
    </div>
  );
}
